import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AuthModalProvider } from './contexts/AuthModalContext';
import LoadingSpinner from './components/shared/LoadingSpinner';

// Layouts — légers, chargés immédiatement (forment le squelette persistant)
import AppLayout from './components/layout/AppLayout';
import ClientLayout from './components/layout/ClientLayout';
import AdminLayout from './components/layout/AdminLayout';
import PublicLayout from './components/layout/PublicLayout';
import GerantEnAttente from './pages/gerant/GerantEnAttente';

// Pages chargées à la demande (code-splitting) — évite que la page
// d'accueil publique attende le téléchargement des dashboards gérant/admin
// (recharts, html5-qrcode...) avant de pouvoir s'afficher.
const PublicAccueil = lazy(() => import('./pages/public/Accueil'));
const ResidenceDetailPublic = lazy(() => import('./pages/public/ResidenceDetail'));

const Dashboard = lazy(() => import('./pages/gerant/Dashboard'));
const Residences = lazy(() => import('./pages/gerant/Residences'));
const ResidenceDetail = lazy(() => import('./pages/gerant/ResidenceDetail'));
const Reservations = lazy(() => import('./pages/gerant/Reservations'));
const Locataires = lazy(() => import('./pages/gerant/Locataires'));
const Paiements = lazy(() => import('./pages/gerant/Paiements'));
const Depenses = lazy(() => import('./pages/gerant/Depenses'));
const ScannerTicket = lazy(() => import('./pages/gerant/ScannerTicket'));
const Profil = lazy(() => import('./pages/gerant/Profil'));
const Equipe = lazy(() => import('./pages/gerant/Equipe'));
const Avis = lazy(() => import('./pages/gerant/Avis'));

const ReceiptTicket = lazy(() => import('./pages/client/ReceiptTicket'));
const MesReservations = lazy(() => import('./pages/client/MesReservations'));
const Favoris = lazy(() => import('./pages/client/Favoris'));
const ProfilClient = lazy(() => import('./pages/client/ProfilClient'));

const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminProprietaires = lazy(() => import('./pages/admin/AdminProprietaires'));
const AdminClients = lazy(() => import('./pages/admin/AdminClients'));
const AdminResidences = lazy(() => import('./pages/admin/AdminResidences'));
const AdminReservations = lazy(() => import('./pages/admin/AdminReservations'));
const AdminParametres = lazy(() => import('./pages/admin/AdminParametres'));
const AdminAvis = lazy(() => import('./pages/admin/AdminAvis'));

function ProtectedRoute({ children, role }) {
  const { currentUser, userProfile } = useAuth();
  if (!currentUser) return <Navigate to="/" replace />;
  // Un sous-gérant utilise les mêmes routes /gerant/* que le gérant principal.
  const rolesAutorises = role === 'gerant' ? ['gerant', 'sous-gerant'] : [role];
  if (role && userProfile && !rolesAutorises.includes(userProfile.role)) {
    if (userProfile.role === 'admin') return <Navigate to="/admin/dashboard" replace />;
    return <Navigate to={['gerant', 'sous-gerant'].includes(userProfile.role) ? '/gerant/dashboard' : '/'} replace />;
  }
  // Compte gérant pas encore validé par l'admin — accès bloqué (voir
  // GerantEnAttente, qui se débloque automatiquement en temps réel).
  // "statutValidation" absent = compte créé avant cette fonctionnalité,
  // non concerné par le blocage. Ne concerne pas les sous-gérants.
  if (userProfile?.role === 'gerant' && userProfile?.statutValidation && userProfile.statutValidation !== 'validé') {
    return <GerantEnAttente />;
  }
  return children;
}

// Certaines pages (Dépenses, Mon équipe) restent réservées au gérant
// principal — un sous-gérant y est renvoyé vers son tableau de bord.
function GerantPrincipalOnly({ children }) {
  const { userProfile } = useAuth();
  if (userProfile?.role === 'sous-gerant') return <Navigate to="/gerant/dashboard" replace />;
  return children;
}

function AdminRoute({ children }) {
  const { currentUser, userProfile } = useAuth();
  if (!currentUser) return <Navigate to="/" replace />;
  if (userProfile && userProfile.role !== 'admin') {
    return <Navigate to="/" replace />;
  }
  return children;
}

// Page d'accueil : publique pour anonymes/clients, redirige gérants/admin
// vers leur propre tableau de bord.
function HomeRoute() {
  const { userProfile } = useAuth();
  if (userProfile?.role === 'admin') return <Navigate to="/admin/dashboard" replace />;
  if (['gerant', 'sous-gerant'].includes(userProfile?.role)) return <Navigate to="/gerant/dashboard" replace />;
  return <PublicAccueil />;
}

function AppRoutes() {
  return (
    <Suspense fallback={<LoadingSpinner />}>
      <Routes>
        {/* Public */}
        <Route path="/" element={<PublicLayout />}>
          <Route index element={<HomeRoute />} />
          <Route path="residence/:id" element={<ResidenceDetailPublic />} />
        </Route>

        {/* Admin */}
        <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
          <Route path="dashboard" element={<AdminDashboard />} />
          <Route path="proprietaires" element={<AdminProprietaires />} />
          <Route path="clients" element={<AdminClients />} />
          <Route path="residences" element={<AdminResidences />} />
          <Route path="reservations" element={<AdminReservations />} />
          <Route path="avis" element={<AdminAvis />} />
          <Route path="parametres" element={<AdminParametres />} />
        </Route>

        {/* Gérant */}
        <Route path="/gerant" element={<ProtectedRoute role="gerant"><AppLayout /></ProtectedRoute>}>
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="residences" element={<Residences />} />
          <Route path="residences/:id" element={<ResidenceDetail />} />
          <Route path="reservations" element={<Reservations />} />
          <Route path="locataires" element={<Locataires />} />
          <Route path="paiements" element={<Paiements />} />
          <Route path="depenses" element={<GerantPrincipalOnly><Depenses /></GerantPrincipalOnly>} />
          <Route path="equipe" element={<GerantPrincipalOnly><Equipe /></GerantPrincipalOnly>} />
          <Route path="avis" element={<GerantPrincipalOnly><Avis /></GerantPrincipalOnly>} />
          <Route path="scanner" element={<ScannerTicket />} />
          <Route path="profil" element={<Profil />} />
        </Route>

        {/* Client — pages nécessitant un compte (recherche + détail résidence sont publics, voir ci-dessus) */}
        <Route path="/client" element={<ProtectedRoute role="client"><ClientLayout /></ProtectedRoute>}>
          <Route path="recu/:id" element={<ReceiptTicket />} />
          <Route path="reservations" element={<MesReservations />} />
          <Route path="favoris" element={<Favoris />} />
          <Route path="profil" element={<ProfilClient />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AuthModalProvider>
          <AppRoutes />
          <Toaster
            position="top-center"
            toastOptions={{
              style: { fontFamily: 'Inter, sans-serif', fontSize: '0.9rem', borderRadius: 10 },
              success: { iconTheme: { primary: '#27AE60', secondary: 'white' } },
              error: { iconTheme: { primary: '#E74C3C', secondary: 'white' } },
            }}
          />
        </AuthModalProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
